import { ResponseError } from '@backstage/errors';
import { getPath, sleep } from '@vielzeug/arsenal';
import { CourierHttpError } from '@vielzeug/courier';

import type { AuditLogApi } from './api';
import type {
  AuditLogEntry,
  AuditLogSearchFilters,
  AuditLogSearchParams,
  EventFieldFilter,
  PageRequest,
  PagedResult,
} from './types';
import { AUDIT_LOG_FIXTURES } from './fixtures';

const MAX_VALUES_PER_FILTER = 10;
const MAX_PAGE_SIZE = 100;
const DEFAULT_LATENCY_MS = 150;

/**
 * Stage-1 mock implementing the real Audit Log service contract in memory:
 * the same filters, caps, sorting (Spring `property,dir`), zero-based paging
 * and ProblemDetail-style 404s, so swapping in {@link AuditLogApiClient} is a
 * config-only change.
 */
export class MockAuditLogApi implements AuditLogApi {
  private readonly entries: AuditLogEntry[];
  private readonly latencyMs: number;
  /**
   * Answer every request with the 403 an unsubscribed tenant gets from
   * the real service, so the onboarding screen can be demoed locally.
   * Seeded from `auditLog.simulateUnsubscribed` and mutable at runtime —
   * the navbar's development toggle flips it.
   */
  simulateUnsubscribed: boolean;

  constructor(options?: {
    entries?: readonly AuditLogEntry[];
    latencyMs?: number;
    now?: Date;
    simulateUnsubscribed?: boolean;
  }) {
    this.entries = options?.entries
      ? [...options.entries]
      : rebaseFixtures(AUDIT_LOG_FIXTURES, options?.now ?? new Date());
    this.latencyMs = options?.latencyMs ?? DEFAULT_LATENCY_MS;
    this.simulateUnsubscribed = options?.simulateUnsubscribed ?? false;
  }

  async searchLogs({
    filters,
    page,
    signal,
  }: AuditLogSearchParams): Promise<PagedResult<AuditLogEntry>> {
    this.validatePage(page);
    this.validateFilters(filters);

    await sleep(this.latencyMs, signal);

    if (this.simulateUnsubscribed) {
      throw await responseError(403, 'Forbidden');
    }

    const matched = this.entries.filter(entry =>
      matchesFilters(entry, filters),
    );
    const sorted = sortEntries(matched, page?.sort);
    return paginate(sorted, page);
  }

  async getLogById(id: string, signal?: AbortSignal): Promise<AuditLogEntry> {
    await sleep(this.latencyMs, signal);

    if (this.simulateUnsubscribed) {
      throw await responseError(403, 'Forbidden');
    }

    const entry = this.entries.find(candidate => candidate.id === id);

    if (!entry) {
      throw problemDetail(404, 'Not Found', `Audit log ${id} not found`);
    }

    return entry;
  }

  private validatePage(page?: PageRequest): void {
    const size = page?.size;
    const number = page?.page;

    if (number !== undefined && number < 0) {
      throw problemDetail(400, 'Bad Request', 'page must be zero or greater');
    }

    if (size !== undefined && (size < 1 || size > MAX_PAGE_SIZE)) {
      throw problemDetail(
        400,
        'Bad Request',
        `size must be between 1 and ${MAX_PAGE_SIZE}`,
      );
    }
  }

  private validateFilters(filters?: AuditLogSearchFilters): void {
    const lists: readonly (readonly string[])[] = [
      filters?.eventTypeIn ?? [],
      filters?.eventByIn ?? [],
    ];
    const eventFilters: readonly EventFieldFilter[] = filters?.event ?? [];

    for (const list of lists) {
      if (list.length > MAX_VALUES_PER_FILTER) {
        throw problemDetail(
          400,
          'Bad Request',
          `filters accept at most ${MAX_VALUES_PER_FILTER} values`,
        );
      }
    }

    if (eventFilters.length > MAX_VALUES_PER_FILTER) {
      throw problemDetail(
        400,
        'Bad Request',
        `event filters accept at most ${MAX_VALUES_PER_FILTER} entries`,
      );
    }
  }
}

function rebaseFixtures(
  entries: readonly AuditLogEntry[],
  now: Date,
): AuditLogEntry[] {
  if (entries.length === 0) return [];

  const newestEventAt = Math.max(
    ...entries.map(entry => Date.parse(entry.eventAt)),
  );
  const offset = now.getTime() - newestEventAt;
  const shift = (instant: string) =>
    new Date(Date.parse(instant) + offset).toISOString();

  return entries.map(entry => ({
    ...entry,
    createdAt: shift(entry.createdAt),
    eventAt: shift(entry.eventAt),
    expiresAt: entry.expiresAt ? shift(entry.expiresAt) : null,
  }));
}

function matchesFilters(
  entry: AuditLogEntry,
  filters?: AuditLogSearchFilters,
): boolean {
  if (!filters) return true;

  if (
    filters.eventTypeIn?.length &&
    !filters.eventTypeIn.includes(entry.eventType)
  ) {
    return false;
  }

  if (filters.eventByIn?.length && !filters.eventByIn.includes(entry.eventBy)) {
    return false;
  }

  if (filters.eventAtStart && entry.eventAt < filters.eventAtStart) {
    return false;
  }

  if (filters.eventAtEnd && entry.eventAt > filters.eventAtEnd) {
    return false;
  }

  return (filters.event ?? []).every(({ field, value }) =>
    fieldEquals(entry.event, field, value),
  );
}

/** Resolves a dotted path (`a.a1`) inside the event payload and compares exactly. */
function fieldEquals(
  payload: Record<string, unknown>,
  path: string,
  value: string | number | boolean,
): boolean {
  return getPath(payload, path) === value;
}

function sortEntries<T extends AuditLogEntry>(
  entries: T[],
  sort?: readonly string[],
): T[] {
  const criteria = (sort ?? ['eventAt,desc']).map(criterion => {
    const [property, direction] = criterion.split(',');
    return {
      property: property ?? 'eventAt',
      descending: direction?.toLowerCase() === 'desc',
    };
  });

  return [...entries].sort((a, b) => {
    for (const { property, descending } of criteria) {
      const left = sortableValue(a, property);
      const right = sortableValue(b, property);

      if (left !== right) {
        return (left < right ? -1 : 1) * (descending ? -1 : 1);
      }
    }

    return 0;
  });
}

function sortableValue(entry: AuditLogEntry, property: string): string {
  switch (property) {
    case 'createdAt':
      return entry.createdAt;
    case 'eventAt':
      return entry.eventAt;
    case 'eventBy':
      return entry.eventBy;
    case 'eventType':
      return entry.eventType;
    case 'id':
      return entry.id;
    default:
      return '';
  }
}

function paginate<T>(items: T[], page?: PageRequest): PagedResult<T> {
  const size = page?.size ?? 25;
  const number = page?.page ?? 0;
  const start = number * size;

  return {
    content: items.slice(start, start + size),
    page: {
      size,
      number,
      totalElements: items.length,
      totalPages: Math.ceil(items.length / size),
    },
  };
}

function problemDetail(
  status: number,
  title: string,
  detail: string,
): CourierHttpError {
  return new CourierHttpError({
    data: { type: 'about:blank', title, status, detail },
    headers: new Headers({ 'content-type': 'application/problem+json' }),
    message: detail,
    method: 'MOCK',
    status,
    url: 'mock://audit-log',
  });
}

// The unsubscribed simulation must trip `isNotSubscribedError`, which keys
// on Backstage's ResponseError — built from a failed ProblemDetail response
// exactly like the real client does (its constructor is private).
async function responseError(status: number, title: string) {
  return ResponseError.fromResponse(
    new Response(
      JSON.stringify({
        type: 'about:blank',
        title,
        status,
        detail: 'Tenant has no audit-log subscription',
      }),
      {
        status,
        headers: { 'content-type': 'application/problem+json' },
      },
    ),
  );
}
